import { COMPANY } from '@jt/domain';

/**
 * The first page of an agreement.
 *
 * ## Why a cover page at all
 *
 * A document that opens straight into "## 1. Who this is between" reads as a
 * print-out. The same words behind a cover read as an instrument — and the
 * difference matters commercially, because a landlord deciding whether to let
 * somebody drill their building, and a bank deciding whether the paper in
 * front of it is real, both judge the first page before they read the second.
 *
 * What a cover is actually *for*, beyond looking serious: it answers, without
 * being read, the four questions asked of any loose document found in a
 * drawer. Who issued it, what is it, who is it with, and when. Everything on
 * this page is one of those four.
 *
 * ## Why the particulars are conditional
 *
 * A P.O. Box, a company number and a KRA PIN are the things that make a cover
 * look like a company's rather than a template's — and they are also the
 * things that must never be guessed. A wrong registration number on a signed
 * agreement is worse than no registration number, because the blank is
 * obviously missing and the wrong one is quietly false. So each line renders
 * only when it is actually set in `COMPANY`, and a cover with nothing but the
 * physical address is the correct output until somebody fills the rest in.
 */

export type CoverParty = {
  /** "The provider", "The landlord", "The customer". */
  role: string;
  name: string;
  /** Address, phone, whatever identifies them. Blank entries are dropped. */
  lines?: Array<string | null | undefined>;
};

export function DocumentCover({
  title,
  reference,
  parties,
  issuedOn,
  standing,
}: {
  title: string;
  reference: string;
  parties: CoverParty[];
  /** ISO date the document was issued, when it has been. */
  issuedOn?: string | null;
  /** One line under the reference: "Executed", "Awaiting signature". */
  standing?: string | null;
}) {
  /*
   * Only the particulars that exist.
   *
   * See the note above: a labelled blank on a legal cover is a liability, and
   * an absent line is not.
   */
  const particulars = [
    COMPANY.poBox ? `P.O. Box ${COMPANY.poBox}${COMPANY.postalCode ? `–${COMPANY.postalCode}` : ''}, ${COMPANY.town}` : null,
    `${COMPANY.town}, ${COMPANY.county}, ${COMPANY.country}`,
    COMPANY.registrationNo ? `Company No. ${COMPANY.registrationNo}` : null,
    COMPANY.kraPin ? `KRA PIN ${COMPANY.kraPin}` : null,
    `${COMPANY.phoneE164} · ${COMPANY.email}`,
    COMPANY.website.replace(/^https?:\/\//, ''),
  ].filter((line): line is string => Boolean(line));

  const issued = issuedOn
    ? new Date(issuedOn).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <section className="document-cover" aria-label="Cover page">
      {/* ── Who issued it ──────────────────────────────────────────── */}
      <header className="cover-issuer">
        {/*
          Empty alt. The legal name is set immediately below in type a screen
          reader will announce anyway, and "GoSpeedNet logo, GoSpeedNet Ltd" is
          the same word twice.
        */}
        <img src="/brand/logo.png" alt="" className="cover-mark" />
        <p className="cover-legal-name">{COMPANY.legalName}</p>
        <p className="cover-particulars">
          {particulars.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </p>
      </header>

      {/* ── What it is ─────────────────────────────────────────────── */}
      <div className="cover-title-block">
        <p className="cover-eyebrow">Agreement</p>
        <h1 className="cover-title">{title}</h1>
        <p className="cover-reference">
          <span className="cover-ref-label">Reference</span>
          <span className="cover-ref-value">{reference}</span>
        </p>
        {standing && <p className="cover-standing">{standing}</p>}
      </div>

      {/* ── Who it is between ──────────────────────────────────────── */}
      <div className="cover-parties">
        <p className="cover-between">Between</p>
        {parties.map((party, index) => (
          <div key={party.role} className="cover-party">
            {/*
              "and" between the parties rather than before each of them. A
              cover that reads "Between / and The provider" has been written by
              a loop rather than by a person.
            */}
            {index > 0 && <p className="cover-and">and</p>}
            <p className="cover-party-role">{party.role}</p>
            <p className="cover-party-name">{party.name}</p>
            {(party.lines ?? [])
              .filter((line): line is string => Boolean(line && line.trim()))
              .map((line) => (
                <p key={line} className="cover-party-line">
                  {line}
                </p>
              ))}
          </div>
        ))}
      </div>

      {/* ── When, and a word about what this copy is ───────────────── */}
      <footer className="cover-foot">
        {issued && (
          <p>
            <span className="cover-foot-label">Dated</span> {issued}
          </p>
        )}
        <p className="cover-foot-note">
          This document is issued by {COMPANY.legalName} and is confidential to the parties named
          above. It is executed electronically under the Business Laws (Amendment) Act, 2020, and
          each signature is recorded with its time and a fingerprint of this exact wording.
        </p>
      </footer>
    </section>
  );
}
